import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { formatCurrency, formatInteger } from '@/lib/format'
import { useDashboardOverview } from '@/hooks/useDashboard'
import { useDashboardPeriod } from './DashboardPeriodContext'
import {
  KpiAlertIcon,
  KpiArrowRightIcon,
  KpiBarChartIcon,
  KpiCalendarIcon,
  KpiEuroIcon,
  KpiStatusDotIcon,
  KpiTrendingDownIcon,
  KpiTrendingUpIcon,
} from '@/components/icons/KpiIcons'

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>
  className: string
  label: string
  value: string
  meta?: React.ReactNode
  extra?: React.ReactNode
  to?: string
  actionLabel?: string
  loading?: boolean
  error?: boolean
}

function KpiCard({
  icon: Icon,
  className,
  label,
  value,
  meta,
  extra,
  to,
  actionLabel,
  loading,
  error,
}: KpiCardProps) {
  const { t } = useTranslation('dashboard')
  const resolvedActionLabel = actionLabel ?? t('kpi.viewDetails')

  const cardClassName = cn(
    'flex min-w-0 flex-col gap-4 overflow-hidden rounded-[20px] border border-[#e7e9ef] p-4 shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]',
    to &&
      'cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)]',
    className
  )

  const content = (
    <>
      <div className='flex h-5 min-w-0 items-center gap-2'>
        <Icon />
        <p
          className='min-w-0 flex-1 truncate font-heading text-xs font-medium leading-[1.4] text-[#5a5a5a]'
          title={label}
        >
          {label}
        </p>
        {to && (
          <span className='inline-flex shrink-0 items-center gap-1 font-sans text-xs font-medium leading-none text-[#3d434e]'>
            {resolvedActionLabel}
            <KpiArrowRightIcon className='size-3' />
          </span>
        )}
      </div>

      <div className='flex min-h-18 flex-col gap-2'>
        {loading ? (
          <Skeleton className='h-9.5 w-28' />
        ) : error ? (
          <p className='text-sm text-destructive'>{t('kpi.unableToLoad')}</p>
        ) : (
          <p className='font-heading text-2xl font-bold leading-[1.2] text-[#171a20]'>
            {value}
          </p>
        )}
        {meta && !loading && !error && meta}
        {extra && !loading && !error && extra}
      </div>
    </>
  )

  if (to) {
    return (
      <Link to={to} aria-label={label} className={cardClassName}>
        {content}
      </Link>
    )
  }

  return <div className={cardClassName}>{content}</div>
}

function StatusTag({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'font-sans text-xs font-medium leading-4 whitespace-nowrap text-[#5a5a5a]',
        className
      )}
    >
      {children}
    </span>
  )
}

function InlineDelta({
  value,
  comparison,
}: {
  value: number | null | undefined
  comparison: string
}) {
  const { t } = useTranslation('dashboard')
  if (value === null || value === undefined) {
    return (
      <span className='text-xs text-muted-foreground'>
        {t('delta.noPriorData')}
      </span>
    )
  }
  const up = value >= 0
  return (
    <span
      className={cn(
        'inline-flex h-5 w-fit self-start items-center gap-1 rounded-md bg-white px-1.5 py-0.5 font-sans text-xs font-medium leading-4',
        up ? 'text-[#047857]' : 'text-destructive'
      )}
    >
      {up ? <KpiTrendingUpIcon /> : <KpiTrendingDownIcon />}
      {Math.abs(Math.round(value))}% {comparison}
    </span>
  )
}

function previousMonthName(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number)
  const prev = new Date(year, month - 2, 1)
  return prev.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
}

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`
}

function formatObjectiveProgress(progress: number | null): string {
  if (progress === null) return '0%'
  return `${progress.toFixed(2)}%`
}

export function KpiStatRow() {
  const { t, i18n } = useTranslation('dashboard')
  const { period, dateFrom, dateTo } = useDashboardPeriod()
  const { data, isLoading, isError } = useDashboardOverview(period)
  const overview = data?.data

  const interventionsToday = overview?.interventions_today ?? 0
  const todayBreakdown = overview?.interventions_today_by_status
  const todayRevenue = overview?.revenue_today ?? 0
  const monthlyRevenue = overview?.revenue_monthly ?? 0
  const monthlyDelta = overview?.revenue_monthly_delta ?? null
  const objective = overview?.monthly_revenue_objective ?? null
  const overdueInvoices = overview?.unpaid_invoices ?? 0
  const overdueInvoicesCurrentMonth =
    overview?.overdue_invoices_current_month ?? 0
  const objectiveProgress = overview?.revenue_today_objective_progress ?? null
  const today = formatLocalDate(new Date())
  const todayRevenueLink = `/invoices?date_from=${today}&date_to=${today}`
  const monthlyRevenueLink = `/invoices?date_from=${dateFrom}&date_to=${dateTo}`

  const interventionsTodayExtra = todayBreakdown ? (
    <div className='flex flex-wrap items-center gap-1'>
      <StatusTag>
        {t('kpi.statusInProgress', { count: todayBreakdown.in_progress })}
      </StatusTag>
      <KpiStatusDotIcon />
      <StatusTag>
        {t('kpi.statusUpcoming', { count: todayBreakdown.upcoming })}
      </StatusTag>
      <KpiStatusDotIcon />
      <StatusTag>
        {t('kpi.statusCompleted', { count: todayBreakdown.completed })}
      </StatusTag>
    </div>
  ) : undefined

  const todayRevenueExtra =
    objective && objective > 0 ? (
      <div className='flex flex-col gap-1'>
        <div className='flex items-center justify-between gap-2 font-sans text-xs font-medium leading-4'>
          <span className='text-[#5a5a5a]'>
            {t('kpi.objective', {
              amount: formatCurrency(objective, 'EUR', 0, 'prefix'),
            })}
          </span>
          <span className='text-[#ff5a1f]'>
            {formatObjectiveProgress(objectiveProgress)}
          </span>
        </div>
        <Progress
          value={Math.min(100, objectiveProgress ?? 0)}
          className='h-2 bg-white'
          indicatorClassName='bg-[#ff5a1f]'
        />
      </div>
    ) : (
      <p className='font-sans text-xs font-medium text-[#5a5a5a]'>
        {t('kpi.objectiveUnset')}
      </p>
    )

  const monthlyRevenueMeta = (
    <InlineDelta
      value={monthlyDelta}
      comparison={t('kpi.vsPreviousMonth', {
        month: previousMonthName(period, i18n.language),
      })}
    />
  )

  const unpaidMeta = (
    <p className='font-sans text-xs font-medium text-[#5a5a5a]'>
      {t('kpi.unpaidInvoicesCount', { count: overdueInvoicesCurrentMonth })}
    </p>
  )

  return (
    <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-4'>
      <KpiCard
        icon={KpiCalendarIcon}
        className='bg-[#ebf1fe]'
        label={t('kpi.interventionsToday')}
        value={formatInteger(interventionsToday)}
        extra={interventionsTodayExtra}
        to='/calendar'
        actionLabel={t('kpi.agenda')}
        loading={isLoading}
        error={isError}
      />
      <KpiCard
        icon={KpiEuroIcon}
        className='bg-[#fff2e8]'
        label={t('kpi.todayRevenue')}
        value={formatCurrency(todayRevenue, 'EUR', 0, 'prefix')}
        extra={todayRevenueExtra}
        to={todayRevenueLink}
        actionLabel={t('kpi.invoices')}
        loading={isLoading}
        error={isError}
      />
      <KpiCard
        icon={KpiBarChartIcon}
        className='bg-[#ecfdf5]'
        label={t('kpi.monthlyRevenue')}
        value={formatCurrency(monthlyRevenue, 'EUR', 0, 'prefix')}
        meta={monthlyRevenueMeta}
        to={monthlyRevenueLink}
        actionLabel={t('kpi.invoices')}
        loading={isLoading}
        error={isError}
      />
      <KpiCard
        icon={KpiAlertIcon}
        className='bg-[#fde9e8]'
        label={t('kpi.unpaidInvoices')}
        value={formatInteger(overdueInvoices)}
        meta={unpaidMeta}
        to='/invoices?status=overdue'
        actionLabel={t('kpi.invoices')}
        loading={isLoading}
        error={isError}
      />
    </div>
  )
}

export default KpiStatRow
